import React, { useState, useEffect } from 'react';
import { ArrowRight, X, Calendar, PawPrint, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';


const WildlifeSanctuaries = () => {
  const navigate = useNavigate();
  const [sanctuaries, setSanctuaries] = useState([]);
  const [selectedPark, setSelectedPark] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/api/sanctuaries`)
      .then(res => res.json())
      .then(data => {
        if (Array.isArray(data)) {
          setSanctuaries(data);
        } else {
          console.error("Backend returned invalid format:", data);
        }
      })
      .catch(err => console.error("Error fetching sanctuaries:", err));
  }, []);

  // Prevent background scrolling when modal is open
  useEffect(() => {
    if (selectedPark) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => { document.body.style.overflow = 'unset'; };
  }, [selectedPark]);

  return (
    <div className="container section">
      <h1 className="section-title">Wildlife Sanctuaries</h1>
      <p className="section-subtitle">
        Explore India's incredible protected habitats and the majestic animals that call them home.
      </p>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
        gap: '2rem',
        marginTop: '3rem'
      }}>
        {sanctuaries.map((park, index) => (
          <motion.div
            key={park.id}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1, duration: 0.5 }}
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              overflow: 'hidden',
              boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            {/* Image */}
            <div style={{ height: '240px', overflow: 'hidden' }}>
              <img
                src={park.image}
                alt={park.name}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>

            {/* Content */}
            <div style={{ padding: '1.5rem', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <h3 style={{ fontSize: '1.4rem', fontWeight: 'bold', color: '#111', margin: 0, paddingRight: '1rem' }}>
                  {park.name}
                </h3>
                <span style={{
                  backgroundColor: '#e6f4ea',
                  color: '#ea580c',
                  padding: '4px 12px',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  whiteSpace: 'nowrap'
                }}>
                  {park.location}
                </span>
              </div>

              <p style={{ color: '#5f6368', fontSize: '1rem', lineHeight: '1.5', marginBottom: '1.5rem' }}>
                {park.description}
              </p>

              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ fontSize: '0.95rem', fontWeight: 'bold', marginBottom: '0.75rem', color: '#000' }}>Highlights:</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {park.highlights.map((highlight, i) => (
                    <span key={i} style={{
                      backgroundColor: '#fce8e6',
                      color: '#c5221f',
                      padding: '4px 12px',
                      borderRadius: '20px',
                      fontSize: '0.85rem',
                      fontWeight: '500'
                    }}>
                      {highlight}
                    </span>
                  ))}
                </div>
              </div>

              {/* Spacer to push button to bottom */}
              <div style={{ flexGrow: 1 }}></div>

              <button
                onClick={() => setSelectedPark(park)}
                style={{
                  width: '100%',
                  padding: '0.85rem',
                  backgroundColor: '#ea580c',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: '0.5rem',
                  cursor: 'pointer',
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#c2410c'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = '#ea580c'}
              >
                Explore <ArrowRight size={18} />
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Modal / Frame Overlay */}
      <AnimatePresence>
        {selectedPark && (
          <div style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 9999,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            padding: '1rem'
          }}>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedPark(null)}
              style={{
                position: 'absolute',
                top: 0, left: 0, right: 0, bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.6)',
                backdropFilter: 'blur(4px)'
              }}
            />

            {/* Modal Content */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              style={{
                backgroundColor: '#fff',
                borderRadius: '16px',
                width: '100%',
                maxWidth: '800px',
                maxHeight: '90vh',
                overflowY: 'auto',
                position: 'relative',
                zIndex: 10000,
                boxShadow: '0 25px 50px -12px rgba(0,0,0,0.5)',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <button
                onClick={() => setSelectedPark(null)}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  backgroundColor: 'rgba(0,0,0,0.5)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  cursor: 'pointer',
                  zIndex: 10,
                  transition: 'background-color 0.2s'
                }}
                onMouseOver={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.8)'}
                onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.5)'}
              >
                <X size={20} />
              </button>

              <div style={{ height: '300px', width: '100%', flexShrink: 0 }}>
                <img src={selectedPark.image} alt={selectedPark.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              </div>

              <div style={{ padding: '2rem' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                  <MapPin size={18} color="#ea580c" />
                  <span style={{ color: '#ea580c', fontWeight: 'bold', fontSize: '0.95rem' }}>{selectedPark.location}</span>
                </div>

                <h2 style={{ fontSize: '2rem', fontWeight: 'bold', color: '#111', margin: '0 0 1.5rem 0' }}>{selectedPark.name}</h2>

                <p style={{ fontSize: '1.05rem', lineHeight: '1.7', color: '#4b5563', marginBottom: '2rem' }}>
                  {selectedPark.longDescription}
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.5rem', backgroundColor: '#f9fafb', padding: '1.5rem', borderRadius: '12px' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <Calendar size={18} color="#4b5563" />
                      <h4 style={{ margin: 0, fontWeight: 'bold', color: '#111' }}>Best Time To Visit</h4>
                    </div>
                    <p style={{ margin: 0, color: '#4b5563', paddingLeft: '1.8rem' }}>{selectedPark.bestTimeToVisit}</p>
                  </div>

                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <PawPrint size={18} color="#4b5563" />
                      <h4 style={{ margin: 0, fontWeight: 'bold', color: '#111' }}>Key Species</h4>
                    </div>
                    <ul style={{ margin: 0, paddingLeft: '2.5rem', color: '#4b5563' }}>
                      {selectedPark.keySpecies.map((species, i) => (
                        <li key={i}>{species}</li>
                      ))}
                    </ul>
                  </div>
                </div>

                <div style={{ marginTop: '2rem', textAlign: 'center' }}>
                  <button
                    onClick={() => {
                      const parkName = selectedPark.name;
                      setSelectedPark(null);
                      navigate(`/book-safari?sanctuary=${encodeURIComponent(parkName)}`);
                    }}
                    style={{
                      padding: '1rem 2rem',
                      backgroundColor: '#ea580c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '30px',
                      fontSize: '1.1rem',
                      fontWeight: 'bold',
                      cursor: 'pointer',
                      boxShadow: '0 4px 14px rgba(0,95,21,0.3)',
                      transition: 'transform 0.2s, box-shadow 0.2s'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,95,21,0.4)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,95,21,0.3)';
                    }}
                  >
                    Book Safari
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WildlifeSanctuaries;
